import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { X, Loader2, BookPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { aiQuotaMonthKey } from "@/hooks/useAiQuota";
import { cn } from "@/lib/utils";

export interface WordInfo {
  word: string;
  reading: string;
  romaji: string;
  partOfSpeech: string;
  meaning: string;
  chineseTranslation?: string;
  example?: string;
  exampleMeaning?: string;
  collocations?: { text: string; meaning: string }[];
  _quota?: { lookupRemaining: number; analyzeRemaining: number };
}

function looksMostlyEnglish(text?: string): boolean {
  if (!text) return false;
  const letters = (text.match(/[A-Za-z]/g) || []).length;
  const cjk = (text.match(/[\u4E00-\u9FFF]/g) || []).length;
  return letters >= 8 && letters > cjk * 2;
}

function getPrimaryMeaning(info: WordInfo): string {
  if (
    looksMostlyEnglish(info.meaning) &&
    info.chineseTranslation &&
    /[\u4E00-\u9FFF]/.test(info.chineseTranslation)
  ) {
    return info.chineseTranslation;
  }
  return info.meaning;
}

// Simple in-memory cache to prevent duplicate requests
const wordCache = new Map<string, WordInfo>();
const pendingRequests = new Map<string, Promise<WordInfo>>();

// AI word lookup using edge function
export const lookupWord = async (text: string): Promise<WordInfo> => {
  // 1. Check cache first
  if (wordCache.has(text)) {
    return wordCache.get(text)!;
  }

  // 2. Check if request is already in flight (deduplication)
  if (pendingRequests.has(text)) {
    return pendingRequests.get(text)!;
  }

  // 3. Create new request
  const requestPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("lookup-word", {
        body: { word: text },
      });

      if (error) {
        console.error("Lookup word error:", error);
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const result = {
        word: data.word || text,
        reading: data.reading || "---",
        romaji: data.romaji || "---",
        partOfSpeech: data.partOfSpeech || "未知",
        meaning: data.meaning || "暂无释义",
        chineseTranslation: data.chineseTranslation,
        example: data.example,
        exampleMeaning: data.exampleMeaning,
        collocations: data.collocations,
      };

      // Cache the successful result
      wordCache.set(text, result);
      return { ...result, _quota: (data as any)?._quota };
    } catch (error) {
      console.error("Failed to lookup word:", error);
      let msg = "查词失败，请稍后重试";
      const status = (error as any)?.context?.status as number | undefined;
      const bodyError =
        (error as any)?.context?.body?.error ||
        (error as any)?.context?.body?.message ||
        (error as any)?.message;
      const errorStr = String(error);
      const isGenericNon2xx =
        typeof bodyError === "string" &&
        bodyError.includes("Edge Function returned a non-2xx status code");

      if (status === 401 || status === 403 || /unauthorized|jwt/i.test(errorStr)) {
        msg = "请先登录后使用 AI 查词";
      } else if (status === 404) {
        msg = "AI 服务未就绪，请稍后重试";
      } else if (status === 429 || errorStr.includes("429")) {
        msg = isGenericNon2xx ? "AI 服务繁忙，请稍后再试" : (bodyError || "请求过于频繁，请稍后再试");
      } else if (status === 503) {
        msg = isGenericNon2xx ? "AI 服务繁忙，请稍后再试" : (bodyError || "AI 服务繁忙，请稍后再试");
      } else if (typeof status === "number" && status >= 500) {
        msg = isGenericNon2xx ? "AI 服务暂不可用，请稍后重试" : (bodyError || "AI 服务暂不可用，请稍后重试");
      } else if (bodyError && !isGenericNon2xx) {
        msg = bodyError;
      } else if (errorStr.includes("Edge Function returned a non-2xx status code")) {
        msg = "AI 服务暂不可用，请稍后重试";
      }

      // Fallback response (do not cache errors, so user can retry)
      return {
        word: text,
        reading: "---",
        romaji: "---",
        partOfSpeech: "未知",
        meaning: msg,
      };
    } finally {
      // Remove from pending requests
      pendingRequests.delete(text);
    }
  })();

  pendingRequests.set(text, requestPromise);
  return requestPromise;
};

interface WordPopupProps {
  word: string;
  position: { x: number; y: number };
  onClose: () => void;
  onAddToVocabulary: (info: WordInfo) => void;
}

export function WordPopup({ 
  word, 
  position, 
  onClose,
  onAddToVocabulary 
}: WordPopupProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [wordInfo, setWordInfo] = useState<WordInfo | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [floatingPos, setFloatingPos] = useState<{ left: number; top: number } | null>(null);
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    const touchPoints = Number(window.navigator.maxTouchPoints || 0);
    const coarsePointer =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    return touchPoints > 0 || coarsePointer;
  }, []);
  const posColorMap: Record<string, string> = {
    名詞: "bg-emerald-100 text-emerald-700 border-emerald-200",
    動詞: "bg-sky-100 text-sky-700 border-sky-200",
    形容詞: "bg-amber-100 text-amber-700 border-amber-200",
    副詞: "bg-purple-100 text-purple-700 border-purple-200",
    助詞: "bg-pink-100 text-pink-700 border-pink-200",
    助動詞: "bg-rose-100 text-rose-700 border-rose-200",
    連体詞: "bg-teal-100 text-teal-700 border-teal-200",
    接続詞: "bg-indigo-100 text-indigo-700 border-indigo-200",
    感動詞: "bg-orange-100 text-orange-700 border-orange-200",
    記号: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const posLegend = [
    { label: "名詞", className: posColorMap["名詞"] },
    { label: "動詞", className: posColorMap["動詞"] },
    { label: "形容詞", className: posColorMap["形容詞"] },
    { label: "副詞", className: posColorMap["副詞"] },
    { label: "助詞", className: posColorMap["助詞"] },
    { label: "助動詞", className: posColorMap["助動詞"] },
    { label: "連体詞", className: posColorMap["連体詞"] },
    { label: "接続詞", className: posColorMap["接続詞"] },
    { label: "感動詞", className: posColorMap["感動詞"] },
    { label: "記号", className: posColorMap["記号"] },
  ];

  useEffect(() => {
    setLoading(true);
    lookupWord(word).then(info => {
      setWordInfo(info);
      if (user && info?._quota) {
        const month = aiQuotaMonthKey();
        const key = ["ai-quota", user.id, month];
        const prev = queryClient.getQueryData(key) as any;
        if (prev) {
          queryClient.setQueryData(key, {
            ...prev,
            lookupRemaining: Number(info._quota?.lookupRemaining ?? prev.lookupRemaining),
            analyzeRemaining: Number(info._quota?.analyzeRemaining ?? prev.analyzeRemaining),
          });
        } else {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      setLoading(false);
    });
  }, [queryClient, user, word]);

  useEffect(() => {
    if (typeof window === "undefined" || isTouchDevice) return;

    const updatePos = () => {
      const node = cardRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const margin = 12;
      let left = position.x + 10;
      let top = position.y + 8;

      if (left + rect.width + margin > window.innerWidth) {
        left = Math.max(margin, position.x - rect.width - 10);
      }
      if (left < margin) left = margin;

      if (top + rect.height + margin > window.innerHeight) {
        top = Math.max(margin, position.y - rect.height - 8);
      }
      if (top < margin) top = margin;

      setFloatingPos({ left, top });
    };

    const rafId = window.requestAnimationFrame(updatePos);
    window.addEventListener("resize", updatePos);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updatePos);
    };
  }, [isTouchDevice, loading, position, wordInfo]);

  const primaryMeaning = useMemo(() => {
    if (!wordInfo) return "";
    return getPrimaryMeaning(wordInfo);
  }, [wordInfo]);

  const showSecondaryChinese = useMemo(() => {
    if (!wordInfo?.chineseTranslation) return false;
    const secondary = wordInfo.chineseTranslation.trim();
    if (!secondary) return false;
    return secondary !== primaryMeaning.trim();
  }, [primaryMeaning, wordInfo]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[320] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        className={cn(
          "bg-card border border-border rounded-xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200",
          isTouchDevice
            ? "fixed left-1/2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] -translate-x-1/2 w-[min(94vw,420px)] max-h-[78dvh] sm:max-h-[82vh]"
            : "fixed w-[min(90vw,380px)] max-h-[78vh]",
        )}
        style={!isTouchDevice && floatingPos ? { left: floatingPos.left, top: floatingPos.top } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-card/95 backdrop-blur-sm">
          <div className="min-w-0 pr-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">AI 查词</div>
            <div className="text-lg font-bold text-foreground truncate">{wordInfo?.word || word}</div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full hover:bg-muted shrink-0"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">AI 正在查询...</span>
            </div>
          ) : wordInfo && (
            <div className="space-y-4 text-center">
              {/* Word and reading */}
              <div className="space-y-1">
                <div className="text-3xl font-bold text-foreground">{wordInfo.word}</div>
                <div className="text-lg text-primary font-medium">{wordInfo.reading}</div>
              </div>

              {/* POS */}
              <div className="flex items-center justify-center gap-2">
                <HoverCard openDelay={120}>
                  <HoverCardTrigger asChild>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium border ${
                        posColorMap[wordInfo.partOfSpeech] || "bg-muted text-muted-foreground border-border"
                      }`}
                    >
                      {wordInfo.partOfSpeech}
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-[220px] p-3">
                    <div className="text-xs font-semibold text-muted-foreground mb-2">词性颜色图例</div>
                    <div className="grid grid-cols-2 gap-2">
                      {posLegend.map((item) => (
                        <span key={item.label} className={`px-2 py-0.5 rounded text-[11px] font-medium border ${item.className}`}>
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>

              {/* Meaning */}
              <div className="py-2">
                <p className="text-foreground text-lg font-medium leading-relaxed">{primaryMeaning}</p>
                {showSecondaryChinese && (
                  <p className="text-sm text-muted-foreground/80 mt-1">{wordInfo.chineseTranslation}</p>
                )}
              </div>

              {/* Example */}
              {wordInfo.example && (
                <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3 text-left border border-border/50">
                  <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider block mb-1.5 font-bold">
                    例句 / Example
                  </span>
                  <p className="leading-relaxed mb-1 text-foreground/90">{wordInfo.example}</p>
                  {wordInfo.exampleMeaning && (
                    <p className="text-muted-foreground/80 text-xs border-t border-border/30 pt-1 mt-1">
                      {wordInfo.exampleMeaning}
                    </p>
                  )}
                </div>
              )}

              {/* Collocations */}
              {Array.isArray(wordInfo.collocations) && wordInfo.collocations.length > 0 && (
                <div className="flex flex-col gap-1.5 pt-1 text-left">
                  {wordInfo.collocations.slice(0, 3).map((c, i) => (
                    <div key={i} className="px-2 py-1.5 bg-muted/50 rounded-md text-xs border border-border/50 flex flex-col">
                      <span className="font-medium text-foreground">{typeof c === "string" ? c : c.text}</span>
                      {typeof c !== "string" && c.meaning && (
                        <span className="text-muted-foreground/80 scale-90 origin-top-left">{c.meaning}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="pt-2">
                <Button
                  type="button"
                  size="lg"
                  className="w-full gap-2 shadow-md"
                  onClick={() => onAddToVocabulary(wordInfo)}
                >
                  <BookPlus className="h-4 w-4" />
                  添加到生词本
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    ,
    document.body,
  );
}
